import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { ReportHeader, ReportIcon, ReportKpiCard, ReportState } from './ReportUi';

type AuditItem = {
  match_reason: string;
  listing_1: string;
  agent_1: string;
  listing_2: string;
  agent_2: string;
  sale_or_rent: string;
  identity_1: string | null;
  identity_2: string | null;
  validated_1: boolean;
  validated_2: boolean;
  market_centre_1: string | null;
  market_centre_2: string | null;
  created_1: string;
  created_2: string;
  address_1: string;
  address_2: string;
};

type AuditResponse = {
  generated_at: string;
  summary: {
    active_listings_scanned: number;
    matched_pairs: number;
    same_address_and_identity: number;
    same_address_only: number;
    same_identity_only: number;
    both_validated: number;
  };
  items: AuditItem[];
};

const REASON_FILTERS = ['All', 'Same ID and address', 'Same ID', 'Same address'] as const;

export default function ListingDuplicateAuditReport() {
  const { token, isRegionalAdmin } = useAuth();
  const [reasonFilter, setReasonFilter] = useState<(typeof REASON_FILTERS)[number]>('Same ID and address');

  const authHeaders = useMemo<Record<string, string>>(
    () => (token ? { Authorization: `Bearer ${token}` } : {} as Record<string, string>),
    [token],
  );

  const { data, isLoading, isError, error } = useQuery<AuditResponse>({
    queryKey: ['listing-duplicate-audit'],
    queryFn: async () => {
      const res = await fetch('/api/listings/duplicate-audit', { headers: authHeaders });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Failed to load the duplicate audit.');
      }
      return res.json() as Promise<AuditResponse>;
    },
    enabled: Boolean(token) && isRegionalAdmin,
    staleTime: 300000,
  });

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    if (reasonFilter === 'All') return items;
    return items.filter((item) => item.match_reason === reasonFilter);
  }, [data, reasonFilter]);

  async function downloadCsv(): Promise<void> {
    const res = await fetch('/api/listings/duplicate-audit?format=csv', { headers: authHeaders });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `listing-duplicate-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (!isRegionalAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="page-title">Listing Duplicate Audit</h1>
        <ReportState type="error" message="This report is available to Regional Admin only." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ReportHeader
        title="Listing Duplicate Audit"
        subtitle="Active listings that share a seller/owner identity number or the same property address."
        context={data ? `Generated ${new Date(data.generated_at).toLocaleString('en-ZA')}` : undefined}
        actions={(
          <button type="button" className="secondary-btn" onClick={() => void downloadCsv()} disabled={!data}>
            Export CSV
          </button>
        )}
      />

      {data && (
        <section className="surface-card p-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <ReportKpiCard label="Active Scanned" value={data.summary.active_listings_scanned.toLocaleString('en-ZA')} icon={<ReportIcon kind="home" />} />
            <ReportKpiCard label="Matched Pairs" value={data.summary.matched_pairs.toLocaleString('en-ZA')} icon={<ReportIcon kind="layers" />} />
            <ReportKpiCard label="Same ID + Address" value={data.summary.same_address_and_identity.toLocaleString('en-ZA')} icon={<ReportIcon kind="target" />} />
            <ReportKpiCard label="Same Address" value={data.summary.same_address_only.toLocaleString('en-ZA')} icon={<ReportIcon kind="home" />} />
            <ReportKpiCard label="Same ID" value={data.summary.same_identity_only.toLocaleString('en-ZA')} icon={<ReportIcon kind="users" />} />
          </div>
        </section>
      )}

      <section className="surface-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {REASON_FILTERS.map((reason) => (
            <button
              key={reason}
              type="button"
              className={`rounded-full border px-3 py-1 text-sm ${reasonFilter === reason ? 'border-red-300 bg-red-50 font-semibold text-red-700' : 'border-slate-300 text-slate-600'}`}
              onClick={() => setReasonFilter(reason)}
            >
              {reason}
            </button>
          ))}
        </div>

        {isLoading && <ReportState type="loading" message="Running the duplicate audit..." />}
        {isError && <ReportState type="error" message={error instanceof Error ? error.message : 'Failed to load the duplicate audit.'} />}
        {!isLoading && !isError && rows.length === 0 && <ReportState type="empty" message="No matching listings for this filter." />}

        {rows.length > 0 && (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Match</th>
                  <th className="px-3 py-2">Listing 1</th>
                  <th className="px-3 py-2">Agent 1</th>
                  <th className="px-3 py-2">Listing 2</th>
                  <th className="px-3 py-2">Agent 2</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">ID Number</th>
                  <th className="px-3 py-2">Address</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((row, index) => (
                  <tr key={`${row.listing_1}-${row.listing_2}-${index}`} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${row.match_reason === 'Same ID and address' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                        {row.match_reason}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">{row.listing_1}</td>
                    <td className="px-3 py-2">{row.agent_1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{row.listing_2}</td>
                    <td className="px-3 py-2">{row.agent_2}</td>
                    <td className="px-3 py-2">{row.sale_or_rent}</td>
                    <td className="px-3 py-2">{row.identity_1 ?? '-'}</td>
                    <td className="px-3 py-2 text-slate-600">{row.address_1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 500 && (
              <p className="mt-2 text-xs text-slate-500">Showing the first 500 of {rows.length.toLocaleString('en-ZA')} pairs. Export the CSV for the full list.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
